import { Controller } from "@hotwired/stimulus"
import db from "../field_db"
import { atRiskImages, statusForRecord, backupOrphan, discardImage, queueDirectUpload, registerUploadSync, photoPreviewBlob, SUBMITTED_GRACE_MS } from "../field_image_queue"
import { compressImageWithRetry, COMPRESSION_FAILURE } from "../field_image_compressor"
import { logError } from "../field_logger"
import { observedFetch, currentLevel } from "../field_network_health"
import { timeoutFor } from "../field_timeout_policy"
import { formatBytes } from "../lib/format_bytes"

// My Photos gallery (W3/W4, ADR-0048 §1).
//
// Renders ONE continuous history as a de-duped UNION of:
//   - at-risk local blobs from IndexedDB (pending_images), rendered from their
//     localUrl, with a visible per-image status (on device / uploading / failed),
//     and a "Back up to WIN" action (W4); and
//   - the tasker's recent server Image records (fetched from the field images
//     endpoint), shown as "safe on WIN".
//
// De-dupe: a local shadow row and its server Image can coexist briefly (the
// shadow is purged on response sync). We key on serverUrl when present, else on
// the local blobKey. When a local row and a server row collide, we prefer the
// "safe on WIN" server version (ADR brief) -- but carry the local blob URL onto
// it, so tap-to-view opens the on-device bytes rather than pulling the original
// back down (rt-client parity: a tasker's own photo is never re-downloaded).
//
// Direct library upload ("Upload from your library") is a two-step flow:
//   1. The native picker selects files; instead of uploading immediately, a
//      batch-details form shows -- the tasker picks a Place (searching the
//      offline cached_places store) and a Client (defaulted from the place).
//   2. Confirm stamps the WHOLE batch with the chosen placeId/clientId
//      (queueDirectUpload), then uploads each file with per-file progress rows.
//
// No HTML is built in JS: tiles, thumbs, and place rows are cloned from
// <template>s in the view.
export default class extends Controller {
  static targets = [
    "loading", "empty", "allClear", "grid", "tiles", "tileTemplate",
    "pendingSection", "pendingTiles",
    "submittedSection", "submittedTiles", "orphanedSection", "orphanedTiles",
    "fileInput", "uploadList", "uploadRowTemplate",
    "uploadForm", "photoCount", "thumbStrip", "selectedThumbTemplate",
    "placeSearch", "placeResults", "placeResultTemplate", "placeEmpty",
    "clientSelect", "confirmBtn"
  ]
  static values = { url: String }

  connect() {
    this._csrf = document.querySelector("[name='csrf-token']")?.content || ""
    this._cameraFilesHandler = (e) => this.cameraFiles(e)
    window.addEventListener("field:photo-camera:files", this._cameraFilesHandler)
    // Card #2790 -- a sync finishing while this page is open (another tab, the
    // service worker's Background Sync, or this tab's own foreground sync)
    // must move the just-uploaded row out of Pending upload without a reload.
    // Same add/remove-listener lifecycle as field_sync_banner_controller.js.
    this._syncCompleteHandler = () => this.render()
    window.addEventListener("field:sync-complete", this._syncCompleteHandler)
    this.render()
  }

  disconnect() {
    window.removeEventListener("field:photo-camera:files", this._cameraFilesHandler)
    window.removeEventListener("field:sync-complete", this._syncCompleteHandler)
    // Free any batch-form preview object URLs if the page is left mid-flow.
    this._revokeThumbUrls()
    this._revokeGalleryUrls()
  }

  async render() {
    // Each render mints fresh gallery blob URLs (see _galleryObjectUrl); the
    // previous render's batch is released here.
    this._revokeGalleryUrls()

    // Paint local images immediately -- offline captures are visible without any
    // network wait. The service worker can hold a fetch for up to 15s before
    // AbortSignal fires; coupling local render to it would block the gallery.
    const local = await this._loadLocal().catch((error) => {
      logError("app", "Photo gallery local load failed", { message: error?.message }).catch(() => {})
      return []
    })
    this._paint(this._buildUnion(local, []))

    // Then enhance with server images (synced photos from WIN). Re-fetch local
    // too in case uploads confirmed between the two reads.
    if (!this.urlValue) return
    try {
      const server = await this._loadServer()
      if (server.length > 0) {
        const freshLocal = await this._loadLocal().catch(() => local)
        this._paint(this._buildUnion(freshLocal, server))
      }
    } catch {
      // Server unavailable (offline or timeout) -- local-only paint already done
    }
  }

  // Back up one at-risk local row to WIN (W4). recordId rides on the tile.
  async backup(event) {
    const button = event.currentTarget
    const tile   = button.closest("[data-field-photos-target='tile']")
    const id     = Number(tile?.dataset.recordId)
    if (!id) return

    button.disabled = true
    button.textContent = "..."

    const result = await backupOrphan(id, this._csrf)

    if (result.ok) {
      // Confirmed safe on WIN and the local row was purged -- re-render the union
      // so the row flips to "safe on WIN" (now sourced from the server side).
      await this.render()
    } else {
      // Not banked -- the local copy is retained. Surface why and let them retry.
      button.disabled = false
      button.textContent = result.reason === "offline" ? "Offline" : "Retry"
    }
  }

  // Step 1 of the direct library upload: files were picked from the native
  // picker. Do NOT upload yet -- stash the files and show the batch-details
  // form (Place search + Client select) so the tasker sets the context for
  // the whole batch first. Thumbnails clone the <template> per file.
  async uploadSelected(event) {
    const files = Array.from(event.target.files || [])
    event.target.value = ""
    if (files.length === 0) return
    await this._startBatchUpload(files)
  }

  // Receives files captured by the in-app camera (standalone mode) and feeds
  // them into the same batch-details flow as the library picker.
  async cameraFiles(event) {
    const files = Array.from(event.detail?.files || [])
    if (files.length === 0) return
    await this._startBatchUpload(files)
  }

  // Opens the in-app camera in standalone mode (no SurveyJS question).
  openCamera() {
    window.dispatchEvent(new CustomEvent("field:photo-camera:open", {
      detail: { question: null, multi: true }
    }))
  }

  // Place search over the offline cached_places store. Typing invalidates any
  // prior selection -- the confirm gate re-opens only via choosePlace. Result
  // rows clone the <template> (no HTML in JS), capped at 8.
  searchPlaces(event) {
    this._selectedPlaceId = null
    this.confirmBtnTarget.disabled = true

    const query = (event.target.value || "").trim().toLowerCase()
    this.placeResultsTarget.replaceChildren()
    if (query === "") {
      this.placeResultsTarget.classList.add("hidden")
      return
    }

    const matches = this._places
      .filter(place => (place.name || "").toLowerCase().includes(query))
      .slice(0, 8)

    for (const place of matches) {
      const fragment = this.placeResultTemplateTarget.content.cloneNode(true)
      const row = fragment.querySelector("button")
      row.dataset.placeId = place.placeId
      const addrRaw = place.address
      const addrStr = (addrRaw && typeof addrRaw === 'object')
        ? [addrRaw.street, addrRaw.city, addrRaw.state, addrRaw.postcode].filter(Boolean).join(', ')
        : (addrRaw || '')
      fragment.querySelector("[data-role='place-name']").textContent = place.name || ""
      fragment.querySelector("[data-role='place-address']").textContent = addrStr
      this.placeResultsTarget.appendChild(fragment)
    }

    this.placeResultsTarget.classList.toggle("hidden", matches.length === 0)
  }

  // A place row was tapped. Lock it in, populate the client select from the
  // place's cached clientId/clientName (if present), and open the confirm gate.
  choosePlace(event) {
    const placeId = event.currentTarget.dataset.placeId
    const place = this._places.find(p => String(p.placeId) === String(placeId))
    if (!place) return

    this._selectedPlaceId = place.placeId
    this.placeSearchTarget.value = place.name || ""
    this.placeResultsTarget.replaceChildren()
    this.placeResultsTarget.classList.add("hidden")

    // Populate the client select from the chosen place only.
    // Cached places from the v3 DB migration may lack clientId -- leave the
    // placeholder in that case (clientId is optional metadata).
    const select = this.clientSelectTarget
    select.replaceChildren(new Option("Select a client", ""))
    if (place.clientId != null) {
      select.appendChild(new Option(place.clientName || String(place.clientId), String(place.clientId)))
      select.value = String(place.clientId)
    }

    this.confirmBtnTarget.disabled = false
  }

  // Dismiss the batch form without uploading. The picked files were never
  // banked, so there is nothing to purge beyond the preview object URLs.
  cancelUpload() {
    this._revokeThumbUrls()
    this._selectedFiles = null
    this._selectedPlaceId = null
    this.uploadFormTarget.classList.add("hidden")
  }

  // Step 2: the batch context is set -- upload. Each photo is compressed,
  // banked in the vault FIRST with the chosen placeId/clientId
  // (queueDirectUpload -- the blob is safe locally before any network attempt,
  // ADR-0048 §3), then uploaded immediately when online via the orphan-backup
  // endpoint. Offline (or on a network failure) the banked row stays queued
  // and Background Sync picks it up. Progress renders one row per file,
  // cloned from the template (no HTML in JS).
  async confirmUpload() {
    if (this._selectedPlaceId == null) return
    const files    = this._selectedFiles || []
    const placeId  = this._selectedPlaceId
    const clientId = this.clientSelectTarget.value || null

    this.uploadFormTarget.classList.add("hidden")
    this._revokeThumbUrls()
    this._selectedFiles = null

    this.uploadListTarget.classList.remove("hidden")

    for (const file of files) {
      const { row, statusEl, sizeEl } = this._buildUploadRow(file.name)
      this.uploadListTarget.appendChild(row)
      try {
        let compressed
        try {
          compressed = await compressImageWithRetry(file)
        } catch (error) {
          // Retry ladder exhausted (or the file exceeded the hard size cap) --
          // don't silently fall back to the uncompressed original (card #2433).
          logError("upload", "Photo compression exhausted", { message: error?.message, cause: error?.cause?.message || error?.cause?.name || null, size: file.size, reason: error?.reason || null, detectedType: error?.detectedType || file.type || null, heicConversionAttempted: !!error?.heicConversionAttempted }).catch(() => {})
          // Card #3363 -- say why: HEIC that couldn't be converted, or a video.
          const skipStatus = {
            [COMPRESSION_FAILURE.HEIC_CONVERSION_FAILED]: "heic_failed",
            [COMPRESSION_FAILURE.VIDEO]:                  "video"
          }[error?.reason] || "too_large"
          this._setUploadStatus(statusEl, skipStatus)
          continue
        }
        // Card #3492 pt.1 -- show the resulting compressed size as soon as
        // it's known, reflecting the raised 30MB cap (#3494).
        if (sizeEl) sizeEl.textContent = formatBytes(compressed.size)
        // Verify the blob is still readable before touching IDB. Expired File handles
        // pass instanceof Blob and have size > 0, but fail when serialised (DataError
        // InvalidBlob). A 1-byte slice read confirms the data is actually accessible.
        try {
          await compressed.slice(0, 1).arrayBuffer()
        } catch {
          logError("upload", "Library upload failed", { message: "InvalidBlob: file handle expired, try again" }).catch(() => {})
          this._setUploadStatus(statusEl, "expired")
          continue
        }
        const { id } = await queueDirectUpload(compressed, { placeId, clientId })

        if (navigator.onLine) {
          const result = await backupOrphan(id, this._csrf)
          if (result.ok) {
            this._setUploadStatus(statusEl, "done")
          } else if (result.reason === "offline") {
            this._setUploadStatus(statusEl, "queued")
            registerUploadSync()
          } else {
            this._setUploadStatus(statusEl, "failed")
          }
        } else {
          this._setUploadStatus(statusEl, "queued")
          registerUploadSync()
        }
      } catch (error) {
        logError("upload", "Library upload failed", { message: error?.message }).catch(() => {})
        this._setUploadStatus(statusEl, "failed")
      }
    }

    await this.render()
  }

  // -- private --

  // Shared batch-details setup used by both uploadSelected (library picker) and
  // cameraFiles (in-app camera). Stashes files, builds the preview strip, and
  // reveals the Place/Client form. Upload runs only after the tasker confirms.
  async _startBatchUpload(files) {
    this._selectedFiles = files
    this._revokeThumbUrls()
    this._thumbUrls = files.map(file => URL.createObjectURL(file))
    this._selectedPlaceId = null

    this._places = (await db.cached_places.toArray())
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""))

    this.clientSelectTarget.replaceChildren(new Option("Select a client", ""))

    this.placeSearchTarget.value = ""
    this.placeSearchTarget.disabled = false
    this.placeResultsTarget.replaceChildren()
    this.placeResultsTarget.classList.add("hidden")
    this.placeEmptyTarget.classList.add("hidden")

    this.photoCountTarget.textContent =
      files.length === 1 ? "1 photo selected" : `${files.length} photos selected`

    this.thumbStripTarget.replaceChildren()
    for (const url of this._thumbUrls) {
      const fragment = this.selectedThumbTemplateTarget.content.cloneNode(true)
      fragment.querySelector("img").src = url
      this.thumbStripTarget.appendChild(fragment)
    }

    this.confirmBtnTarget.textContent =
      files.length === 1 ? "Upload 1 photo" : `Upload ${files.length} photos`
    this.confirmBtnTarget.disabled = true

    if (this._places.length === 0) {
      this.placeEmptyTarget.classList.remove("hidden")
      this.placeSearchTarget.disabled = true
    }

    this.uploadFormTarget.classList.remove("hidden")
    this.uploadListTarget.classList.add("hidden")
  }

  // Build the client <select> options from the distinct clients across the
  // cached places: a placeholder first, then one option per clientId (de-duped,
  // sorted by name). document.createElement-style Option construction is plain
  // DOM creation, not HTML-in-JS.
  _populateClientSelect() {
    const select = this.clientSelectTarget
    select.replaceChildren(new Option("Select a client", ""))

    const seen = new Set()
    const clients = []
    for (const place of this._places) {
      if (place.clientId == null || seen.has(String(place.clientId))) continue
      seen.add(String(place.clientId))
      clients.push({ id: place.clientId, name: place.clientName || "" })
    }
    clients.sort((a, b) => a.name.localeCompare(b.name))
    for (const client of clients) {
      select.appendChild(new Option(client.name, String(client.id)))
    }
    select.value = ""
  }

  _revokeThumbUrls() {
    for (const url of this._thumbUrls || []) {
      try { URL.revokeObjectURL(url) } catch { /* ok */ }
    }
    this._thumbUrls = []
  }

  // A pending_images record's stored localUrl is the blob: URL minted when it
  // was queued. blob: URLs die with the document that minted them, so after a
  // PWA relaunch that string is dead -- the thumb would error and fall back to
  // the server original, and the viewer would show a broken image. The Blob
  // itself survives in IndexedDB, so mint a fresh URL off it per render and
  // release the batch on the next render / disconnect.
  //
  // Card #3995 -- vault rows hold no Blob; their bytes are read from
  // pending_blobs (photoPreviewBlob), legacy rows hand back their embedded blob.
  async _galleryObjectUrl(record) {
    const blob = await photoPreviewBlob(record)
    if (!blob) return record.localUrl || null
    const url = URL.createObjectURL(blob)
    this._galleryUrls = this._galleryUrls || []
    this._galleryUrls.push(url)
    return url
  }

  _revokeGalleryUrls() {
    for (const url of this._galleryUrls || []) {
      try { URL.revokeObjectURL(url) } catch { /* ok */ }
    }
    this._galleryUrls = []
  }

  _buildUploadRow(filename) {
    const fragment = this.uploadRowTemplateTarget.content.cloneNode(true)
    const row      = fragment.querySelector("[data-field-photos-target='uploadRow']")
    const name     = fragment.querySelector("[data-field-photos-target='uploadName']")
    const sizeEl   = fragment.querySelector("[data-field-photos-target='uploadSize']")
    const statusEl = fragment.querySelector("[data-field-photos-target='uploadStatus']")
    name.textContent = filename
    this._setUploadStatus(statusEl, "uploading")
    return { row, statusEl, sizeEl }
  }

  _setUploadStatus(statusEl, state) {
    const presentation = {
      uploading: { label: "Uploading",  classes: ["bg-amber-100",   "text-amber-700"] },
      done:      { label: "Uploaded",   classes: ["bg-emerald-100", "text-emerald-700"] },
      queued:    { label: "Queued, will upload when online", classes: ["bg-slate-200", "text-slate-600"] },
      failed:    { label: "Failed",     classes: ["bg-red-100",     "text-red-700"] },
      expired:   { label: "File expired — try again", classes: ["bg-amber-100", "text-amber-700"] },
      too_large: { label: "Photo too large — try again", classes: ["bg-red-100", "text-red-700"] },
      heic_failed: { label: "HEIC photo couldn't be converted. Save it as JPEG and try again.", classes: ["bg-red-100", "text-red-700"] },
      video:     { label: "Videos can't be added. Use the photo instead.", classes: ["bg-red-100", "text-red-700"] }
    }[state]
    statusEl.classList.remove("bg-amber-100", "text-amber-700", "bg-emerald-100", "text-emerald-700", "bg-slate-200", "text-slate-600", "bg-red-100", "text-red-700")
    statusEl.textContent = presentation.label
    statusEl.classList.add(...presentation.classes)
  }

  async _loadLocal() {
    const [rows, pendingResponses] = await Promise.all([
      atRiskImages(),
      db.pending_responses.toArray()
    ])
    const activeResponseIds = new Set(pendingResponses.map(r => String(r.responseId)))
    return Promise.all(rows.map(async record => {
      const status = statusForRecord(record)
      const submitted = !!record.submittedAt
      // Confirmed-uploaded queued row, never stamped submitted, and no queued
      // response still referencing it -- nothing will ever clean it up except
      // the tasker. Surface it in the Orphaned section with a delete action.
      // Card #3827 (B1) -- not an attached row: the server holds it in its
      // answer and it's kept on purpose until the response is submitted (or
      // purgeStale's 7-day sweep), so it shows as safe in the main grid.
      const orphaned = !submitted &&
        record.kind === "queued" &&
        record.status === "uploaded" &&
        !!record.serverUrl &&
        !record.attachedAt &&
        !activeResponseIds.has(String(record.responseId))
      // Card #3827 -- no readable bytes left; nothing to show or back up.
      const lost = status.state === "lost"
      const localUrl = lost ? null : await this._galleryObjectUrl(record)
      return {
        source:      "local",
        recordId:    record.id,
        key:         record.serverUrl || record.blobKey,
        thumbSrc:    localUrl,
        // On-device bytes -- the viewer opens these directly; a blob: URL is
        // already instant, so no preview step.
        localUrl,
        preview:     null,
        serverUrl:   record.serverUrl || null,
        submitted,
        submittedAt: record.submittedAt || null,
        orphaned,
        lost,
        atRisk:      record.kind === "queued" && !submitted && !orphaned && !lost && status.state !== "safe",
        status,
        // Card #2790 -- when this local row was captured/queued, for sorting
        // the Pending upload section and the main grid newest-first.
        timestamp:   record.queuedAt || null
      }
    }))
  }

  async _loadServer() {
    if (!this.urlValue) return []
    const response = await observedFetch(this.urlValue, {
      headers: { Accept: "application/json", "X-CSRF-Token": this._csrf },
      credentials: "include",
      signal: AbortSignal.timeout(timeoutFor("json_read_gallery", currentLevel()))
    }, { kind: "json_read", sizeClass: "small" })
    if (!response.ok) return []
    const data = await response.json()
    return (data.images || []).map(image => ({
      source:    "server",
      key:       image.url,
      thumbSrc:  image.thumbnail || image.url,
      // Filled in by _buildUnion when a local row holds this photo's bytes.
      localUrl:  null,
      // 800x800 variant the viewer paints first while the full original loads.
      preview:   image.preview || null,
      serverUrl: image.url,
      submitted: false,
      // A server image with no response is an orphan (direct library upload
      // awaiting admin attribution) -- routed to the orphaned section.
      orphaned:  image.response_id == null,
      atRisk:    false,
      status:    { state: "safe" },
      // Card #2790 -- server capture time, for sorting the main grid newest-first.
      timestamp: image.created_at || null
    }))
  }

  // De-dupe the union, preferring the server "safe on WIN" row when both a local
  // and a server row share a key (serverUrl). Server rows are added first so a
  // colliding local row is dropped. Submitted and orphaned local rows are split
  // into their own sections and own their serverUrl tile. Orphaned SERVER rows
  // (response-less direct uploads awaiting admin attribution) join the orphaned
  // section too.
  //
  // Card #2790 -- the remaining main-grid rows are split again: at-risk rows
  // (not yet synced -- the only copy of that field work) surface in their own
  // Pending upload section so reopening the app shows what is outstanding
  // first, instead of being mixed in with already-synced photos. Both the
  // pending section and the synced-only grid are sorted most-recent-first.
  _buildUnion(local, server) {
    const localSubmitted = local.filter(row => row.submitted)
    const localOrphaned  = local.filter(row => row.orphaned)
    const rest           = local.filter(row => !row.submitted && !row.orphaned)

    // A submitted/orphaned local row owns its serverUrl tile -- drop the server
    // twin from the main grid so the image appears once, in its section.
    const claimed = new Set([...localSubmitted, ...localOrphaned].map(row => row.key))

    // A local row that loses the collision below still holds the photo's bytes
    // on-device (e.g. a just-taken photo in an in-progress response). Carry its
    // blob URL onto the winning server row so tap-to-view opens local bytes.
    const localUrlByKey = new Map(rest.filter(row => row.localUrl).map(row => [row.key, row.localUrl]))

    const seen = new Set()
    const main = []
    const orphaned = [...localOrphaned]
    for (const row of server) {
      if (claimed.has(row.key) || seen.has(row.key)) continue
      seen.add(row.key)
      if (localUrlByKey.has(row.key)) row.localUrl = localUrlByKey.get(row.key)
      if (row.orphaned) { orphaned.push(row) } else { main.push(row) }
    }
    for (const row of rest) {
      if (seen.has(row.key)) continue
      seen.add(row.key)
      main.push(row)
    }

    const byTimestampDesc = (a, b) => this._timeOf(b) - this._timeOf(a)
    const pending = main.filter(row => row.atRisk).sort(byTimestampDesc)
    const synced  = main.filter(row => !row.atRisk).sort(byTimestampDesc)

    return { pending, synced, submitted: localSubmitted, orphaned }
  }

  // A row with no parseable timestamp sorts last rather than throwing the
  // whole sort off with NaN.
  _timeOf(row) {
    const time = row.timestamp ? new Date(row.timestamp).getTime() : NaN
    return Number.isFinite(time) ? time : 0
  }

  _paint({ pending, synced, submitted, orphaned }) {
    this.loadingTarget.classList.add("hidden")

    const total = pending.length + synced.length + submitted.length + orphaned.length
    const anyAtRisk = pending.length > 0 || synced.some(row => row.lost)
    this.allClearTarget.classList.toggle("hidden", anyAtRisk || total === 0)

    if (total === 0) {
      this.emptyTarget.classList.remove("hidden")
      this.gridTarget.classList.add("hidden")
      this.pendingSectionTarget.classList.add("hidden")
      this.submittedSectionTarget.classList.add("hidden")
      this.orphanedSectionTarget.classList.add("hidden")
      return
    }

    this.emptyTarget.classList.add("hidden")

    this.pendingSectionTarget.classList.toggle("hidden", pending.length === 0)
    this.pendingTilesTarget.replaceChildren()
    for (const row of pending) this.pendingTilesTarget.appendChild(this._buildTile(row))

    this.gridTarget.classList.toggle("hidden", synced.length === 0)
    this.tilesTarget.replaceChildren()
    for (const row of synced) this.tilesTarget.appendChild(this._buildTile(row))

    this.submittedSectionTarget.classList.toggle("hidden", submitted.length === 0)
    this.submittedTilesTarget.replaceChildren()
    for (const row of submitted) this.submittedTilesTarget.appendChild(this._buildTile(row))

    this.orphanedSectionTarget.classList.toggle("hidden", orphaned.length === 0)
    this.orphanedTilesTarget.replaceChildren()
    for (const row of orphaned) this.orphanedTilesTarget.appendChild(this._buildTile(row))
  }

  _buildTile(row) {
    const fragment = this.tileTemplateTarget.content.cloneNode(true)
    const tile        = fragment.querySelector("[data-field-photos-target='tile']")
    const thumb       = fragment.querySelector("[data-field-photos-target='thumb']")
    const placeholder = fragment.querySelector("[data-field-photos-target='placeholder']")
    const pill        = fragment.querySelector("[data-field-photos-target='statusPill']")
    const btn         = fragment.querySelector("[data-field-photos-target='backupBtn']")

    // Graceful thumbnail fallback (W3 robustness): the thumbnail is a variant URL
    // that can 500 when libvips can't process the blob. On the first load error
    // fall back to the full image url; if THAT also fails, show the neutral
    // placeholder. Never a broken-image glyph. The full url rides on a data
    // attribute and the one-shot flag guards against an infinite error loop when
    // the thumbnail and full url resolve to the same broken resource.
    if (row.recordId) tile.dataset.recordId = row.recordId

    // Lost (card #3827): no bytes to show. The notice replaces the thumbnail,
    // asks for a retake, and the tile offers Delete. Nothing to view.
    if (row.lost) {
      thumb.classList.add("hidden")
      fragment.querySelector("[data-field-photos-target='lostNotice']").classList.remove("hidden")
      tile.dataset.lost = "true"
    } else {
      const fullUrl = row.serverUrl && row.serverUrl !== row.thumbSrc ? row.serverUrl : null
      if (fullUrl) thumb.dataset.fullUrl = fullUrl
      thumb.addEventListener("error", this._onThumbError.bind(this))
      thumb.src = row.thumbSrc
    }

    // Wire tap-to-view: on-device bytes first. A tasker's own photo is never
    // downloaded back (rt-client parity) -- the local blob IS the compressed
    // bytes that were uploaded, so the viewer opens instantly at identical
    // quality. The server URL is only for server-only rows. Skip when there is
    // no usable URL at all (placeholder-only row).
    const viewerUrl = row.lost ? null : (row.localUrl || row.serverUrl || row.thumbSrc)
    if (viewerUrl) {
      thumb.dataset.action = "click->pwa-image-viewer#open"
      thumb.dataset.pwaImageViewerUrlParam = viewerUrl
      // Progressive load for server-only rows: the viewer shows the preview
      // variant first and swaps to the full original once it has streamed in.
      // Skip when the preview IS the full url (legacy rows) -- nothing to stage.
      if (!row.localUrl && row.preview && row.preview !== viewerUrl) {
        thumb.dataset.pwaImageViewerPreviewParam = row.preview
      }
      thumb.classList.add("cursor-pointer")
    }

    const displayState = this._displayState(row)
    for (const el of pill.querySelectorAll("[data-state]")) {
      el.classList.toggle("hidden", el.dataset.state !== displayState)
    }
    // A lost tile's red indicator carries the meaning on its own for screen
    // readers (card #3827); other states are unchanged.
    if (row.lost) {
      const failedEl = pill.querySelector("[data-state='failed']")
      failedEl.setAttribute("role", "img")
      failedEl.setAttribute("aria-label", "Photo couldn't be saved")
    }
    if (displayState === "submitted" && row.submittedAt) {
      const submittedEl = pill.querySelector("[data-state='submitted']")
      const msLeft = SUBMITTED_GRACE_MS - (Date.now() - new Date(row.submittedAt).getTime())
      const hours  = Math.max(1, Math.ceil(msLeft / 3_600_000))
      submittedEl.textContent = msLeft > 0 ? `${hours}h` : "Done"
    }

    // Only at-risk rows can be backed up. Safe rows hide the button.
    if (row.atRisk) {
      btn.classList.remove("hidden")
    }

    // Delete frees DEVICE space only -- server-only orphan rows have no local bytes to free.
    const deleteBtn = fragment.querySelector("[data-field-photos-target='deleteBtn']")
    if ((row.submitted || row.orphaned || row.lost) && row.recordId) {
      deleteBtn.classList.remove("hidden")
    }
    if (row.lost) deleteBtn.setAttribute("aria-label", "Remove unsaved photo")

    return fragment
  }

  // Explicitly discard one local banked copy. Submitted/orphaned rows: their
  // bytes are already on WIN, so this only frees device space. Lost rows: the
  // photo couldn't be saved, so this clears the reminder.
  async delete(event) {
    const tile = event.currentTarget.closest("[data-field-photos-target='tile']")
    const id   = Number(tile?.dataset.recordId)
    if (!id) return
    const message = tile.dataset.lost
      ? "Remove this photo? It couldn't be saved, so you'll need to retake it in the survey."
      : "Remove this photo from your device? It stays safely on WIN."
    if (!confirm(message)) return
    await discardImage(id)
    await this.render()
  }

  // Thumbnail load failed. Two-stage, one-shot fallback:
  //   1. If a distinct full image url is available and we haven't tried it yet,
  //      swap src to it (the un-variant url) and let it have one go.
  //   2. Otherwise (no full url, or the full url also failed) hide the broken img
  //      and reveal the neutral placeholder for this tile.
  // The dataset flags guard against an infinite error loop.
  _onThumbError(event) {
    const thumb = event.currentTarget
    const fullUrl = thumb.dataset.fullUrl

    if (fullUrl && !thumb.dataset.triedFull) {
      thumb.dataset.triedFull = "true"
      thumb.src = fullUrl
      return
    }

    // Both the thumbnail and the full url are unusable -- show the placeholder.
    thumb.classList.add("hidden")
    const tile = thumb.closest("[data-field-photos-target='tile']")
    const placeholder = tile?.querySelector("[data-field-photos-target='placeholder']")
    placeholder?.classList.remove("hidden")
  }

  _displayState(row) {
    if (row.submitted) return "submitted"
    switch (row.status.state) {
      case "uploading": return "uploading"
      case "lost":      return "failed"
      case "failed":
        return row.status.reason === "offline" ? "pending" : "failed"
      case "safe": return "safe"
      default: return "pending"
    }
  }
}
